/**
 * 🟥 CUADRO NEGRO V7 — EnvVarsPanel.
 *
 * Modal para gestionar variables de entorno (GitHub Actions Secrets + Variables).
 *   - Secrets cifrados con libsodium (server-side), nunca se exponen.
 *   - Variables visibles (no secretas).
 *   - Crear / actualizar / eliminar.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  KeyRound, X, Loader2, Plus, Save, Trash2, Eye, EyeOff,
  Lock, Unlock,
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function EnvVarsPanel({ moduleId, githubSource, isEs = true, onClose }) {
  const [vars, setVars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newIsSecret, setNewIsSecret] = useState(true);
  const [revealValue, setRevealValue] = useState(false);

  const repoLabel = githubSource ? `${githubSource.owner}/${githubSource.repo}` : '';

  const fetchVars = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/env-vars`, { params: { module_id: moduleId } });
      setVars(res.data?.vars || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Env vars falló: ${detail}` : `Env vars failed: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const id = setTimeout(() => { fetchVars(); }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const handleSave = async () => {
    const key = (newKey || '').trim().toUpperCase();
    if (!key) {
      toast.error(isEs ? 'Key requerida' : 'Key required');
      return;
    }
    if (!newValue) {
      toast.error(isEs ? 'Valor requerido' : 'Value required');
      return;
    }
    setBusy('save');
    try {
      await axios.post(`${API}/env-vars/set`, {
        module_id: moduleId, key, value: newValue, is_secret: newIsSecret,
      });
      toast.success(
        isEs
          ? `${newIsSecret ? 'Secret' : 'Variable'} ${key} guardada`
          : `${newIsSecret ? 'Secret' : 'Variable'} ${key} saved`
      );
      setShowForm(false);
      setNewKey(''); setNewValue(''); setRevealValue(false);
      fetchVars();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      if (detail === 'invalid_env_key') {
        toast.error(isEs ? 'Key inválida (usa MAYUSCULAS_CON_GUIONES_BAJOS)' : 'Invalid key');
      } else if (detail === 'reserved_env_key') {
        toast.error(isEs ? 'Las keys que comienzan con GITHUB_ están reservadas' : 'GITHUB_-prefixed keys are reserved');
      } else {
        toast.error(isEs ? `Guardar falló: ${detail}` : `Save failed: ${detail}`);
      }
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (v) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      isEs ? `¿Eliminar ${v.is_secret ? 'secret' : 'variable'} ${v.key}?` : `Delete ${v.key}?`
    )) return;
    setBusy(`del:${v.key}`);
    try {
      await axios.post(`${API}/env-vars/delete`, {
        module_id: moduleId, key: v.key, is_secret: v.is_secret,
      });
      toast.success(isEs ? `${v.key} eliminada` : `${v.key} deleted`);
      fetchVars();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Borrar falló: ${detail}` : `Delete failed: ${detail}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[8500] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="env-vars-panel"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-3xl h-[88vh] bg-gradient-to-b from-slate-900 to-slate-950 border border-amber-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-gradient-to-r from-amber-900/40 to-yellow-900/30">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-300" />
            <div>
              <h2 className="text-base font-bold text-white">{isEs ? 'Variables de entorno' : 'Environment Variables'}</h2>
              <p className="text-[11px] text-amber-300/80">{repoLabel} · GitHub Actions Secrets + Variables</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowForm((v) => !v)}
              data-testid="env-toggle-create"
              className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-amber-600 hover:bg-amber-500 text-white"
            >
              <Plus className="w-3.5 h-3.5" /> {isEs ? 'Añadir' : 'Add'}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="env-close-btn"
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        {showForm && (
          <div className="border-b border-slate-800 bg-slate-900/60 p-4 space-y-2" data-testid="env-create-form">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value.toUpperCase())}
                placeholder="MY_API_KEY"
                className="flex-1 px-3 py-2 text-sm bg-slate-950 border border-slate-700 rounded-md text-white font-mono focus:border-amber-500 focus:outline-none"
                data-testid="env-key-input"
                disabled={busy === 'save'}
              />
              <label className="flex items-center gap-1.5 text-xs text-slate-300 px-2 py-2 bg-slate-900 rounded-md border border-slate-700">
                <input
                  type="checkbox"
                  checked={newIsSecret}
                  onChange={(e) => setNewIsSecret(e.target.checked)}
                  data-testid="env-is-secret-toggle"
                  className="accent-amber-500"
                />
                {newIsSecret ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                {isEs ? 'Secret' : 'Secret'}
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type={revealValue ? 'text' : 'password'}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder={isEs ? 'valor (cifrado al enviar)' : 'value (encrypted on send)'}
                className="flex-1 px-3 py-2 text-sm bg-slate-950 border border-slate-700 rounded-md text-white font-mono focus:border-amber-500 focus:outline-none"
                data-testid="env-value-input"
                disabled={busy === 'save'}
              />
              <button
                type="button"
                onClick={() => setRevealValue((v) => !v)}
                className="p-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300"
                title={revealValue ? 'Ocultar' : 'Mostrar'}
              >
                {revealValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setNewKey(''); setNewValue(''); setRevealValue(false); }}
                disabled={busy === 'save'}
                className="px-3 py-1.5 text-xs rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300"
              >
                {isEs ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={busy === 'save' || !newKey || !newValue}
                data-testid="env-save-btn"
                className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white"
              >
                {busy === 'save' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                {isEs ? 'Guardar' : 'Save'}
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-12 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : vars.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-12">
              {isEs ? 'No hay variables de entorno aún.' : 'No environment variables yet.'}
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="env-vars-list">
              {vars.map((v) => (
                <div
                  key={`${v.is_secret ? 'sec' : 'var'}:${v.key}`}
                  className="flex items-center gap-3 p-2.5 rounded-md bg-slate-900 border border-slate-800 hover:border-amber-500/30"
                  data-testid={`env-row-${v.key}`}
                >
                  {v.is_secret
                    ? <Lock className="w-4 h-4 text-amber-300 shrink-0" />
                    : <Unlock className="w-4 h-4 text-emerald-300 shrink-0" />}
                  <div className="flex-1 min-w-0 grid grid-cols-2 gap-2">
                    <div className="font-mono text-xs text-white truncate">{v.key}</div>
                    <div className="font-mono text-[11px] text-slate-400 truncate">{v.value_preview}</div>
                  </div>
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 shrink-0">
                    {v.is_secret ? 'secret' : 'var'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDelete(v)}
                    disabled={busy === `del:${v.key}`}
                    data-testid={`env-delete-${v.key}`}
                    className="p-1.5 rounded-md text-rose-400 hover:text-white hover:bg-rose-600 disabled:opacity-60"
                    title={isEs ? 'Eliminar' : 'Delete'}
                  >
                    {busy === `del:${v.key}`
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <footer className="px-5 py-2.5 border-t border-slate-800 bg-slate-900/60">
          <p className="text-[11px] text-slate-500 italic text-center">
            {isEs
              ? '🔒 Los secrets se cifran con la public-key del repo (libsodium) antes de enviarse. GitHub nunca devuelve sus valores.'
              : '🔒 Secrets are encrypted with the repo public-key (libsodium). GitHub never returns the value.'}
          </p>
        </footer>
      </div>
    </div>
  );
}
